import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";
import { PlatoChip } from "@ats/src/components/shared/PlatoMark";

/**
 * PlatoHeaderNavButton — general-purpose nav-styled button for Plato headers.
 *
 * Outlined nav affordance in the style of PlatoCtaButton's "View Plato review":
 * the gradient Plato chip, a label, and a trailing chevron that signals it takes
 * you somewhere. Reusable for any Plato-header nav action (e.g. "Buy credits").
 */
export interface PlatoHeaderNavButtonProps {
  label: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
}

const PlatoHeaderNavButton: React.FunctionComponent<PlatoHeaderNavButtonProps> = ({ label, onClick, disabled }) => {
  return (
    <Styled.Button type="button" onClick={onClick} disabled={disabled}>
      <PlatoChip size={22} />
      {label}
      <Styled.Chevron>
        <Icon name="chevron-right" />
      </Styled.Chevron>
    </Styled.Button>
  );
};

export default PlatoHeaderNavButton;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Chevron = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoHeaderNavButton_Chevron;
    display: inline-flex;
    align-items: center;
    margin-left: 0;
    color: ${t.dark ? t.color.gray[500] : t.color.gray[400]};
    transition: transform 0.15s ease;

    svg {
      display: block;
      width: 16px;
      height: 16px;
    }
  `;
});

Styled.Button = styled.button((props: any) => {
  const t: any = props.theme;
  return css`
    label: PlatoHeaderNavButton;
    display: inline-flex;
    align-items: center;
    gap: ${t.spacing[2]};
    flex-shrink: 0;
    ${[t.text.sm, t.text.semibold]}
    white-space: nowrap;
    cursor: pointer;

    height: 36px;
    padding: 0 ${t.spacing[2]};
    ${t.rounded.sm}
    border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
    background: ${t.dark ? t.color.gray[800] : t.color.white};
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    transition: border-color 0.18s ease, box-shadow 0.2s ease;
    &:hover {
      border-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]};
      box-shadow: 0 1px 3px ${t.dark ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.06)"};
    }
    &:hover ${Styled.Chevron} {
      transform: translateX(1px);
    }
    &:focus-visible {
      outline: none;
      border-color: ${t.dark ? t.color.gray[600] : t.color.gray[300]};
      box-shadow: 0 0 0 3px ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    }

    &:disabled {
      opacity: 0.55;
      cursor: default;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
      ${Styled.Chevron} {
        transition: none;
      }
    }
  `;
});
